import {
  useGetAlphaBscWsStatusQuery,
  useGetAlphaBscWsAlertsQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * AlphaBscWsAlertsCard — real-time price-spike alerts from the
 * Binance Alpha WebSocket watcher. Polls /api/alpha-bsc/ws/alerts every
 * 20s; the watcher itself runs as a persistent server thread.
 *
 * Header shows live connection state + how many symbols are being
 * tracked. Body lists recent spike events with from/to price + %.
 */
export function AlphaBscWsAlertsCard() {
  const status = useGetAlphaBscWsStatusQuery(undefined, { pollingInterval: 30000 });
  const alerts = useGetAlphaBscWsAlertsQuery(30, { pollingInterval: 20000 });

  const s: any = status.data || {};
  const rows: any[] = alerts.data?.alerts || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span>WebSocket spike alerts</span>
          <div className="flex items-center gap-2 text-sm font-normal">
            <Badge tone={s.running ? "good" : "bad"}>
              {s.running ? "● live" : "✕ disconnected"}
            </Badge>
            <span className="text-muted">
              tracking {s.tracked_symbols ?? 0} symbols
              {s.last_seen_count != null && (
                <> · seen {s.last_seen_count}</>
              )}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {alerts.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted">
            No spike alerts yet. Watcher fires when a tracked symbol moves
            ≥5% within a 5-minute window.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-72 overflow-y-auto border border-border rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-bg2">
                <tr className="text-left text-muted">
                  <th className="py-1 px-2">Time</th>
                  <th className="py-1 px-2">Symbol</th>
                  <th className="py-1 px-2">Event</th>
                  <th className="py-1 px-2 text-right">From</th>
                  <th className="py-1 px-2 text-right">To</th>
                  <th className="py-1 px-2 text-right">Δ%</th>
                  <th className="py-1 px-2">Gemini</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any) => {
                  const isUp = r.event === "spike_up";
                  const change = Number(r.change_pct);
                  return (
                    <tr key={r.id} className="border-t border-border/40">
                      <td className="py-1 px-2 text-muted">
                        {(r.ts || "").slice(5, 19).replace("T", " ")}
                      </td>
                      <td className="py-1 px-2 font-mono font-semibold">{r.symbol}</td>
                      <td className="py-1 px-2">
                        <Badge tone={isUp ? "good" : "bad"}>
                          {isUp ? "▲ up" : "▼ down"}
                        </Badge>
                      </td>
                      <td className="py-1 px-2 text-right font-mono">${Number(r.from_price).toFixed(6)}</td>
                      <td className="py-1 px-2 text-right font-mono">${Number(r.to_price).toFixed(6)}</td>
                      <td className={`py-1 px-2 text-right font-mono ${isUp ? "text-emerald-400" : "text-rose-400"}`}>
                        {change > 0 ? "+" : ""}{change.toFixed(2)}%
                      </td>
                      <td className="py-1 px-2 text-xs">
                        {r.consulted_gemini ? (
                          <Badge tone="info">{r.gemini_action || "consulted"}</Badge>
                        ) : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
